<script>
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { required, helpers, requiredIf } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import WootDateTimePicker from 'dashboard/components/ui/DateTimePicker.vue';
import CampaignContactGroupsAPI from 'dashboard/api/campaignContactGroups';
import { format } from 'date-fns';

const ensureArray = value => {
  if (!value) {
    return [];
  }
  if (Array.isArray(value)) {
    return value;
  }
  return [value];
};

const extractTemplateBody = template => {
  if (!template) return '';
  if (template.components && Array.isArray(template.components)) {
    const body = template.components.find(
      component => component.type === 'BODY' && component.text
    );
    if (body) {
      return body.text;
    }
  }
  if (template.content) {
    return template.content;
  }
  if (template.data) {
    return template.data;
  }
  return '';
};

const parsePlaceholders = template => {
  const text = extractTemplateBody(template);
  const matches = text.match(/\{\{(\d+)\}\}/g) || [];
  const unique = [...new Set(matches.map(item => item.replace(/[{}]/g, '')))]
    .map(Number)
    .sort((a, b) => a - b)
    .map(String);
  return unique;
};

const buildTemplateMetadata = template => {
  if (!template) {
    return {};
  }

  const metadata = {};
  if (template.namespace) {
    metadata.namespace = template.namespace;
  }
  if (template.template_id) {
    metadata.template_id = template.template_id;
  }
  if (template.id) {
    metadata.template_id = metadata.template_id || template.id;
  }
  if (template.quality_score) {
    metadata.quality_score = template.quality_score;
  }
  if (template.provider_metadata || template.meta) {
    metadata.provider_metadata = template.provider_metadata || template.meta;
  }
  if (template.components) {
    metadata.components = template.components;
  }
  if (template.container_meta) {
    metadata.container_meta = template.container_meta;
  }
  return metadata;
};

export default {
  name: 'TemplateOneOffForm',
  components: {
    WootDateTimePicker,
  },
  props: {
    selectedContacts: {
      type: Array,
      default: () => [],
    },
    groupsVersion: {
      type: Number,
      default: 0,
    },
    editingCampaign: {
      type: Object,
      default: null,
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  emits: ['close', 'submitted', 'clear-contacts', 'remove-contact'],
  data() {
    return {
      title: '',
      selectedInbox: null,
      selectedTemplate: null,
      placeholders: [],
      placeholderValues: {},
      preloadedVariableMapping: null,
      previewResults: [],
      previewLoading: false,
      sendNow: true,
      scheduledAt: null,
      templateCategory: null,
      templateMediaFile: null,
      contactGroups: [],
      contactGroupsLoading: false,
      selectedGroup: null,
      selectedContactsDisplayLimit: 50,
      selectedContactsScrollObserver: null,
      autoResolveConversation: false,
    };
  },
  computed: {
    ...mapGetters({
      allInboxes: 'inboxes/getInboxes',
      uiFlags: 'campaigns/getUIFlags',
    }),
    eligibleInboxes() {
      return this.allInboxes.filter(inbox =>
        ['Channel::Whatsapp', 'Channel::Iungo'].includes(inbox.channel_type)
      );
    },
    templateCategories() {
      if (!this.selectedInbox) {
        return [];
      }
      const templates = this.selectedInbox.message_templates || [];
      const categories = templates
        .map(template => this.templateCategoryOf(template))
        .filter(Boolean);
      return [...new Set(categories)];
    },
    templateOptions() {
      if (!this.selectedInbox) {
        return [];
      }
      const { channel_type: channelType, message_templates: templates = [] } =
        this.selectedInbox;
      const filtered = templates.filter(template =>
        this.templateCategory ? this.templateCategoryOf(template) === this.templateCategory : true
      );
      return filtered.map(template => ({
        ...template,
        provider:
          channelType === 'Channel::Iungo' ? 'iungo' : 'whatsapp',
      }));
    },
    templateProvider() {
      if (!this.selectedInbox) {
        return null;
      }
      return this.selectedInbox.channel_type === 'Channel::Iungo'
        ? 'iungo'
        : 'whatsapp';
    },
    variableMappingValid() {
      if (!this.placeholders.length) {
        return true;
      }
      return this.placeholders.every(placeholder => {
        const value = this.placeholderValues[placeholder];
        return value !== null && value !== undefined && String(value).trim().length > 0;
      });
    },
    selectedContactIds() {
      return this.selectedContacts.map(contact => contact.id);
    },
    selectedGroupId() {
      return this.selectedGroup ? this.selectedGroup.id : null;
    },
    selectedContactsLabel() {
      const parts = [];

      if (this.selectedGroup) {
        parts.push(
          this.$t('CAMPAIGN.ADD.CONTACTS.GROUP_SELECTOR.SUMMARY_LABEL', {
            name: this.selectedGroup.name,
            count: this.selectedGroup.contacts_count || 0,
          })
        );
      }

      if (this.selectedContacts.length) {
        const names = this.selectedContacts
          .slice(0, 3)
          .map(contact => contact.name || contact.phone_number || contact.email || contact.id);
        const more = this.selectedContacts.length - names.length;
        parts.push(
          more > 0 ? `${names.join(', ')} +${more}` : names.join(', ')
        );
      }

      if (!parts.length) {
        return this.$t('CAMPAIGN.ADD.CONTACTS.NO_SELECTION');
      }

      return parts.join(' • ');
    },
    hasAudience() {
      if (this.selectedGroupId) {
        return true;
      }
      if (this.selectedContactIds.length) {
        return true;
      }
      return false;
    },
    canPreview() {
      return (
        this.selectedTemplate &&
        this.variableMappingValid &&
        this.hasAudience
      );
    },
    templateRequiresMedia() {
      if (!this.selectedTemplate) return false;
      const category = this.templateCategoryOf(this.selectedTemplate);
      return ['media', 'video', 'image', 'document'].includes(category);
    },
    isGroupSelectorDisabled() {
      return this.selectedContacts.length > 0;
    },
    hasSelectedContactsFromPage() {
      return this.selectedContacts.length > 0;
    },
    displayedSelectedContacts() {
      return this.selectedContacts.slice(0, this.selectedContactsDisplayLimit);
    },
    hasMoreSelectedContacts() {
      return this.selectedContacts.length > this.selectedContactsDisplayLimit;
    },
  },
  validations() {
    return {
      title: { required },
      selectedInbox: { required },
      selectedTemplate: { required },
      placeholderValues: {
        isComplete: helpers.withMessage(
          this.$t('CAMPAIGN.ADD.TEMPLATE.MAPPING_REQUIRED'),
          () => this.variableMappingValid
        ),
      },
      scheduledAt: {
        required: requiredIf(function requireSchedule() {
          return !this.sendNow;
        }),
      },
    };
  },
  watch: {
    selectedInbox() {
      this.selectedTemplate = null;
      this.placeholders = [];
      this.placeholderValues = {};
      this.preloadedVariableMapping = null;
      this.templateCategory = null;
      this.templateMediaFile = null;
    },
    selectedTemplate(newTemplate) {
      this.templateMediaFile = null;
      this.handleTemplateChange(newTemplate);
    },
    templateCategory() {
      this.selectedTemplate = null;
      this.templateMediaFile = null;
    },
    groupsVersion() {
      this.fetchContactGroups();
    },
    selectedContacts: {
      handler(newContacts, oldContacts) {
        // Quando contatos são selecionados, limpar o grupo selecionado
        if (newContacts.length > 0 && this.selectedGroup) {
          this.selectedGroup = null;
        }
        // Ajustar o limite de exibição se necessário
        if (newContacts.length === 0) {
          this.selectedContactsDisplayLimit = 50;
        } else if (newContacts.length < this.selectedContactsDisplayLimit) {
          this.selectedContactsDisplayLimit = Math.max(50, newContacts.length);
        }
        // Atualizar observer quando contatos mudam
        this.$nextTick(() => {
          this.updateSelectedContactsObserver();
        });
      },
      immediate: true,
    },
    selectedGroup(newGroup) {
      // Quando um grupo é selecionado, limpar os contatos selecionados
      if (newGroup && this.selectedContacts.length > 0) {
        this.$emit('clear-contacts');
      }
    },
    editingCampaign: {
      handler(newCampaign) {
        if (newCampaign && newCampaign.id) {
          this.loadCampaignData(newCampaign);
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.initialize();
    this.setupSelectedContactsScroll();
    // Carregar dados da campanha se estiver editando
    if (this.editingCampaign && this.editingCampaign.id) {
      this.loadCampaignData(this.editingCampaign);
    }
  },
  beforeUnmount() {
    if (this.selectedContactsScrollObserver) {
      this.selectedContactsScrollObserver.disconnect();
    }
  },
  methods: {
    initialize() {
      this.fetchContactGroups();
    },
    formatPlaceholder(label) {
      return `{{${label}}}`;
    },
    initializePlaceholderValues(placeholders, existingMapping = null) {
      const mapping = existingMapping || this.preloadedVariableMapping || null;
      const currentValues = this.placeholderValues || {};
      const values = {};
      placeholders.forEach(placeholder => {
        let value = '';
        if (mapping && mapping[placeholder]) {
          const config = mapping[placeholder];
          if (config && config.source === 'static') {
            value = config.value || '';
          }
        } else if (currentValues[placeholder]) {
          value = currentValues[placeholder];
        }
        values[placeholder] = value;
      });
      this.placeholderValues = { ...values };
      if (!existingMapping && this.preloadedVariableMapping) {
        this.preloadedVariableMapping = null;
      }
    },
    handleTemplateChange(template) {
      if (!template) {
        this.placeholders = [];
        this.placeholderValues = {};
        return;
      }
      const placeholderList = parsePlaceholders(template);
      this.placeholders = placeholderList;
      this.initializePlaceholderValues(placeholderList);
    },
    updatePlaceholderValue(placeholder, value) {
      this.placeholderValues[placeholder] = value;
    },
    formatTemplateLabel(template) {
      if (!template) return '';
      const language = template.language || template.language_code;
      const category = (template.category || template.template_category || '')
        .toString()
        .toUpperCase();
      const status = (template.status || 'approved').toString().toUpperCase();
      return `${template.name || template.element_name} · ${language} · ${category} · ${status}`;
    },
    templateBodyPreview() {
      if (!this.selectedTemplate) return '';
      const rawText = extractTemplateBody(this.selectedTemplate);
      if (!rawText) {
        return '';
      }
      if (!this.placeholders.length) {
        return rawText;
      }
      return this.placeholders.reduce((text, placeholder) => {
        const value = (this.placeholderValues[placeholder] || '').toString();
        const replacement = value.trim().length ? value : this.formatPlaceholder(placeholder);
        const pattern = new RegExp(`\\{\\{\\s*${placeholder}\\s*\\}\\}`, 'g');
        return text.replace(pattern, replacement);
      }, rawText);
    },
    async onPreview() {
      if (!this.canPreview) {
        useAlert(
          this.$t('CAMPAIGN.ADD.TEMPLATE.VALIDATION_INCOMPLETE')
        );
        return;
      }
      try {
        this.previewLoading = true;
        const payload = this.buildFormData();
        const result = await this.$store.dispatch('campaigns/preview', payload);
        this.previewResults = result || [];
        if (!this.previewResults.length) {
          useAlert(this.$t('CAMPAIGN.ADD.TEMPLATE.PREVIEW_EMPTY'));
        }
      } catch (error) {
        const errorMessage =
          error?.response?.data?.error ||
          this.$t('CAMPAIGN.ADD.API.ERROR_MESSAGE');
        useAlert(errorMessage);
      } finally {
        this.previewLoading = false;
      }
    },
    buildVariableMapping() {
      if (!this.placeholders.length) {
        return {};
      }
      const mapping = {};
      this.placeholders.forEach(placeholder => {
        const value = (this.placeholderValues[placeholder] ?? '').toString().trim();
        mapping[placeholder] = {
          source: 'static',
          value,
        };
      });
      return mapping;
    },
    buildFormData() {
      const formData = new FormData();
      formData.append('campaign[title]', this.title);
      formData.append('campaign[inbox_id]', this.selectedInbox.id);
      formData.append('campaign[send_now]', this.sendNow);

      if (!this.sendNow && this.scheduledAt) {
        const formatted = format(new Date(this.scheduledAt), "yyyy-MM-dd'T'HH:mm:ssxxx");
        formData.append('campaign[scheduled_at]', formatted);
      }

      const templatePayload = this.selectedTemplate || {};
      const category = (templatePayload.category || templatePayload.template_category || '').toUpperCase();
      const language =
        templatePayload.language ||
        templatePayload.language_code ||
        templatePayload.locale;

      formData.append('campaign[template][provider]', this.templateProvider);
      formData.append(
        'campaign[template][name]',
        templatePayload.name || templatePayload.element_name
      );
      formData.append('campaign[template][language]', language || 'en');
      if (category) {
        formData.append('campaign[template][category]', category);
      }
      if (templatePayload.status) {
        formData.append('campaign[template][status]', templatePayload.status);
      }

      const metadata = buildTemplateMetadata(templatePayload);
      Object.keys(metadata).forEach(key => {
        const value = metadata[key];
        if (value !== undefined && value !== null) {
          const stringValue =
            typeof value === 'object' ? JSON.stringify(value) : value;
          formData.append(`campaign[template][metadata][${key}]`, stringValue);
        }
      });

      const variableMapping = this.buildVariableMapping();
      Object.entries(variableMapping).forEach(([placeholder, config]) => {
        Object.entries(config).forEach(([key, value]) => {
          formData.append(`campaign[variable_mapping][${placeholder}][${key}]`, value);
        });
      });

      if (this.selectedGroupId) {
        formData.append('campaign[audience][group_ids][]', this.selectedGroupId);
      }

      this.selectedContactIds.forEach(id => {
        formData.append('campaign[audience][contact_ids][]', id);
      });

      if (this.templateMediaFile) {
        formData.append('campaign[template_media]', this.templateMediaFile);
      }

      formData.append('campaign[auto_resolve_conversation]', this.autoResolveConversation);

      return formData;
    },
    buildUpdatePayload() {
      // Para update, usar objeto simples ao invés de FormData
      const payload = {
        title: this.title,
        inbox_id: this.selectedInbox.id,
        send_now: this.sendNow,
      };

      if (!this.sendNow && this.scheduledAt) {
        const formatted = format(new Date(this.scheduledAt), "yyyy-MM-dd'T'HH:mm:ssxxx");
        payload.scheduled_at = formatted;
      }

      const templatePayload = this.selectedTemplate || {};
      const category = (templatePayload.category || templatePayload.template_category || '').toUpperCase();
      const language =
        templatePayload.language ||
        templatePayload.language_code ||
        templatePayload.locale;

      payload.template = {
        provider: this.templateProvider,
        name: templatePayload.name || templatePayload.element_name,
        language: language || 'en',
      };

      if (category) {
        payload.template.category = category;
      }
      if (templatePayload.status) {
        payload.template.status = templatePayload.status;
      }

      const metadata = buildTemplateMetadata(templatePayload);
      if (Object.keys(metadata).length > 0) {
        payload.template.metadata = metadata;
      }

      payload.variable_mapping = this.buildVariableMapping();

      payload.audience = {};
      if (this.selectedGroupId) {
        payload.audience.group_ids = [this.selectedGroupId];
      }

      if (this.selectedContactIds.length > 0) {
        payload.audience.contact_ids = this.selectedContactIds;
      }

      payload.auto_resolve_conversation = this.autoResolveConversation;

      // Nota: template_media não é suportado em update
      return payload;
    },
    async onSubmit() {
      this.v$.$touch();
      if (this.v$.$invalid || !this.hasAudience) {
        return;
      }
      try {
        if (this.editingCampaign && this.editingCampaign.id) {
          // Modo edição
          const payload = this.buildUpdatePayload();
          await this.$store.dispatch('campaigns/update', {
            id: this.editingCampaign.id,
            ...payload,
          });
          useAlert(this.$t('CAMPAIGN.EDIT.API.SUCCESS_MESSAGE'));
        } else {
          // Modo criação - usar FormData
          const payload = this.buildFormData();
          await this.$store.dispatch('campaigns/create', payload);
          useAlert(this.$t('CAMPAIGN.ADD.API.SUCCESS_MESSAGE'));
        }
        this.$emit('submitted');
      } catch (error) {
        const errorMessage =
          error?.response?.data?.error ||
          (this.editingCampaign && this.editingCampaign.id
            ? this.$t('CAMPAIGN.EDIT.API.ERROR_MESSAGE')
            : this.$t('CAMPAIGN.ADD.API.ERROR_MESSAGE'));
        useAlert(errorMessage);
      }
    },
    loadCampaignData(campaign) {
      if (!campaign || !campaign.id) {
        return;
      }

      // Carregar título
      this.title = campaign.title || '';

      // Carregar inbox
      if (campaign.inbox) {
        const eligibleInboxes = this.allInboxes.filter(inbox =>
          ['Channel::Whatsapp', 'Channel::Iungo'].includes(inbox.channel_type)
        );
        this.selectedInbox = eligibleInboxes.find(
          inbox => inbox.id === campaign.inbox.id
        );
      }

      // Carregar template após inbox estar selecionado
      this.$nextTick(() => {
        if (campaign.template && this.selectedInbox) {
          const templates = this.selectedInbox.message_templates || [];
          const templateName = campaign.template.name;
          const templateLanguage = campaign.template.language;
          this.selectedTemplate = templates.find(
            t =>
              (t.name === templateName || t.element_name === templateName) &&
              (t.language === templateLanguage || t.language_code === templateLanguage)
          );
          if (campaign.template.category) {
            this.templateCategory = campaign.template.category.toLowerCase();
          }
        }

        // Carregar variable mapping
        if (campaign.variable_mapping) {
          this.preloadedVariableMapping = campaign.variable_mapping;
          if (this.placeholders.length) {
            this.initializePlaceholderValues(this.placeholders, campaign.variable_mapping);
          }
        }

        // Carregar scheduled_at
        if (campaign.scheduled_at) {
          this.sendNow = false;
          this.scheduledAt = new Date(campaign.scheduled_at * 1000);
        } else {
          this.sendNow = true;
        }

        // Carregar audience
        if (campaign.audience) {
          if (campaign.audience.group_ids && campaign.audience.group_ids.length > 0) {
            // Buscar grupo
            const groupId = campaign.audience.group_ids[0];
            this.fetchContactGroups().then(() => {
              this.$nextTick(() => {
                this.selectedGroup = this.contactGroups.find(g => g.id === groupId);
              });
            });
          }
          // Nota: contact_ids não são carregados aqui pois não temos acesso direto aos contatos
        }
      });
    },
    onClose() {
      this.$emit('close');
    },
    async fetchContactGroups() {
      this.contactGroupsLoading = true;
      try {
        const { data } = await CampaignContactGroupsAPI.list();
        this.contactGroups = data.groups || [];
        if (this.selectedGroup) {
          const stillPresent = this.contactGroups.find(group => group.id === this.selectedGroup.id);
          if (!stillPresent) {
            this.selectedGroup = null;
          } else {
            this.selectedGroup = stillPresent;
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      } finally {
        this.contactGroupsLoading = false;
      }
    },
    clearSelectedGroup() {
      this.selectedGroup = null;
    },
    removeContact(contactId) {
      this.$emit('remove-contact', contactId);
    },
    contactDisplayValue(contact) {
      return (
        contact.name ||
        contact.phone_number ||
        contact.email ||
        contact.identifier ||
        contact.id
      );
    },
    templateCategoryOf(template) {
      if (!template) {
        return 'text';
      }

      const explicitType = (template.templateType || template.template_type || '').toString().toLowerCase();
      if (explicitType) {
        if (explicitType === 'media') {
          const mediaType = (template.mediaType || template.templateTypeMedia || template.template_type_media || '').toString().toLowerCase();
          if (mediaType) {
            return mediaType;
          }
          return 'media';
        }
        if (['text', 'video', 'image', 'document', 'audio', 'interactive'].includes(explicitType)) {
          return explicitType;
        }
      }

      const headerComponent = (template.components || []).find(component => component.type === 'HEADER');
      if (headerComponent && headerComponent.format) {
        return headerComponent.format.toString().toLowerCase();
      }

      return 'text';
    },
    templateCategoryLabel(category) {
      if (!category) {
        return this.$t('CAMPAIGN.ADD.TEMPLATE.CATEGORIES.ALL');
      }
      const key = `CAMPAIGN.ADD.TEMPLATE.CATEGORIES.${category.toUpperCase()}`;
      return this.$te(key) ? this.$t(key) : category;
    },
    onTemplateMediaChange(event) {
      const [file] = event.target.files || [];
      if (file) {
        this.templateMediaFile = file;
      }
      event.target.value = '';
    },
    clearTemplateMedia() {
      this.templateMediaFile = null;
    },
    setupSelectedContactsScroll() {
      this.$nextTick(() => {
        const scrollContainer = this.$el.querySelector('.selected-contacts-scroll-container');
        if (!scrollContainer) return;

        const observerOptions = {
          root: scrollContainer,
          rootMargin: '50px',
          threshold: 0.1,
        };

        const observerCallback = (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting && this.hasMoreSelectedContacts) {
              this.selectedContactsDisplayLimit += 50;
            }
          });
        };

        this.selectedContactsScrollObserver = new IntersectionObserver(observerCallback, observerOptions);
        this.updateSelectedContactsObserver();
      });
    },
    updateSelectedContactsObserver() {
      this.$nextTick(() => {
        const scrollContainer = this.$el.querySelector('.selected-contacts-scroll-container');
        if (!scrollContainer || !this.selectedContactsScrollObserver) return;

        const sentinel = scrollContainer.querySelector('.selected-contacts-sentinel');
        if (sentinel) {
          if (this.hasMoreSelectedContacts) {
            this.selectedContactsScrollObserver.observe(sentinel);
          } else {
            this.selectedContactsScrollObserver.unobserve(sentinel);
          }
        }
      });
    },
  },
};
</script>

<template>
  <div class="template-campaign-form p-4 space-y-6">
    <form class="space-y-6" @submit.prevent="onSubmit">
      <div class="grid gap-3">
        <label class="woot-form-label">
          {{ $t('CAMPAIGN.ADD.FORM.TITLE.LABEL') }}
        </label>
        <input
          v-model="title"
          type="text"
          class="woot-input"
          :class="{ 'has-error': v$.title.$error }"
          :placeholder="$t('CAMPAIGN.ADD.TEMPLATE.NAME_PLACEHOLDER')"
        />
        <span v-if="v$.title.$error" class="message">
          {{ $t('CAMPAIGN.ADD.FORM.TITLE.ERROR') }}
        </span>
      </div>

      <div class="grid gap-3">
        <label class="woot-form-label">
          {{ $t('CAMPAIGN.ADD.FORM.INBOX.LABEL') }}
        </label>
        <multiselect
          v-model="selectedInbox"
          :options="eligibleInboxes"
          track-by="id"
          label="name"
          :class="{ 'has-multi-select-error': v$.selectedInbox.$error }"
          :searchable="true"
          :placeholder="$t('CAMPAIGN.ADD.TEMPLATE.SELECT_INBOX')"
        />
        <span v-if="v$.selectedInbox.$error" class="message">
          {{ $t('CAMPAIGN.ADD.TEMPLATE.INBOX_REQUIRED') }}
        </span>
      </div>

      <div class="grid gap-3">
        <label class="woot-form-label">
          {{ $t('CAMPAIGN.ADD.TEMPLATE.SELECT_TEMPLATE') }}
        </label>
        <multiselect
          v-model="selectedTemplate"
          :options="templateOptions"
          track-by="name"
          :custom-label="template => formatTemplateLabel(template)"
          :searchable="true"
          :placeholder="$t('CAMPAIGN.ADD.TEMPLATE.SELECT_TEMPLATE_PLACEHOLDER')"
          :class="{ 'has-multi-select-error': v$.selectedTemplate.$error }"
        />
        <span v-if="v$.selectedTemplate.$error" class="message">
          {{ $t('CAMPAIGN.ADD.TEMPLATE.TEMPLATE_REQUIRED') }}
        </span>
        <div
          v-if="selectedTemplate"
          class="border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-slate-900"
        >
          <h4 class="font-semibold text-sm text-slate-600">
            {{ $t('CAMPAIGN.ADD.TEMPLATE.PREVIEW') }}
          </h4>
          <pre class="whitespace-pre-wrap rounded bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 p-3 text-sm text-slate-700 dark:text-slate-200">
{{ templateBodyPreview() }}
          </pre>
        </div>
        <div v-if="selectedTemplate && placeholders.length" class="grid gap-3 mt-4">
          <label class="woot-form-label">
            {{ $t('CAMPAIGN.ADD.TEMPLATE.MAPPING_TITLE') }}
          </label>
          <div class="border border-slate-200 dark:border-slate-700 rounded divide-y divide-slate-200 dark:divide-slate-700">
            <div
              v-for="placeholder in placeholders"
              :key="placeholder"
              class="p-3 grid gap-2"
            >
              <div class="flex items-center justify-between">
                <span class="font-medium text-sm text-slate-700 dark:text-slate-200">
                  {{ formatPlaceholder(placeholder) }}
                </span>
              </div>
              <input
                type="text"
                class="woot-input"
                :placeholder="$t('CAMPAIGN.ADD.TEMPLATE.PLACEHOLDER_VALUE_PLACEHOLDER')"
                :value="placeholderValues[placeholder] || ''"
                @input="updatePlaceholderValue(placeholder, $event.target.value)"
              />
            </div>
          </div>
          <span v-if="v$.placeholderValues.$error" class="message">
            {{ $t('CAMPAIGN.ADD.TEMPLATE.MAPPING_REQUIRED') }}
          </span>
          <p class="text-xs text-slate-500 dark:text-slate-400">
            {{ $t('CAMPAIGN.ADD.TEMPLATE.MAPPING_HELP') }}
          </p>
        </div>
      </div>

      <div class="grid gap-3">
        <label class="woot-form-label">
          {{ $t('CAMPAIGN.ADD.CONTACTS.TITLE') }}
        </label>
        <div 
          v-if="selectedGroup"
          class="rounded border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-3 text-sm text-amber-700 dark:text-amber-300"
        >
          <strong>{{ $t('CAMPAIGN.ADD.CONTACTS.GROUP_SELECTOR.DISABLED_HINT') }}</strong>
        </div>
        <div 
          v-else-if="selectedContacts.length > 0"
          class="rounded border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-3"
        >
          <div class="flex items-center justify-between mb-2">
            <strong class="text-sm text-slate-700 dark:text-slate-200">
              {{ $t('CAMPAIGN.ADD.CONTACTS.SELECTED') }}:
            </strong>
            <span class="text-xs text-slate-500 dark:text-slate-400">
              {{ $t('CAMPAIGN.ADD.CONTACTS.SELECTED_COUNT', { count: selectedContacts.length }) }}
            </span>
          </div>
          <div 
            class="selected-contacts-scroll-container flex flex-wrap gap-2 mt-2"
            style="max-height: 200px; overflow-y: auto;"
          >
            <span
              v-for="contact in displayedSelectedContacts"
              :key="`selected-contact-${contact.id}`"
              class="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm text-slate-700 dark:text-slate-200"
            >
              <span>{{ contactDisplayValue(contact) }}</span>
              <button
                type="button"
                class="text-slate-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                :title="$t('CAMPAIGN.ADD.CONTACTS.REMOVE_CONTACT')"
                @click.prevent="removeContact(contact.id)"
              >
                <fluent-icon icon="dismiss-circle" size="14" />
              </button>
            </span>
            <div 
              v-if="hasMoreSelectedContacts"
              class="selected-contacts-sentinel w-full"
              style="height: 1px;"
            />
          </div>
          <p v-if="hasMoreSelectedContacts" class="text-xs text-slate-500 dark:text-slate-400 mt-2">
            {{ $t('CAMPAIGN.ADD.CONTACTS.SCROLL_TO_SEE_MORE', { remaining: selectedContacts.length - selectedContactsDisplayLimit }) }}
          </p>
        </div>
        <div 
          v-else
          class="rounded border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-3 text-sm text-slate-500 dark:text-slate-400"
        >
          {{ $t('CAMPAIGN.ADD.CONTACTS.NO_SELECTION') }}
        </div>
        <p v-if="!selectedGroup" class="text-xs text-slate-500 dark:text-slate-400">
          {{ $t('CAMPAIGN.ADD.CONTACTS.MODAL_HELP') }}
        </p>
      </div>

      <div class="grid gap-3">
        <label class="woot-form-label">
          {{ $t('CAMPAIGN.ADD.CONTACTS.GROUP_SELECTOR.LABEL') }}
        </label>
        <multiselect
          v-model="selectedGroup"
          :options="contactGroups"
          track-by="id"
          label="name"
          :allow-empty="true"
          :searchable="true"
          :loading="contactGroupsLoading"
          :disabled="isGroupSelectorDisabled"
          :placeholder="$t('CAMPAIGN.ADD.CONTACTS.GROUP_SELECTOR.PLACEHOLDER')"
        />
        <div
          v-if="selectedGroup"
          class="flex flex-wrap items-center gap-2 text-xs text-slate-600 dark:text-slate-300"
        >
          <span>
            {{ $t('CAMPAIGN.ADD.CONTACTS.GROUP_SELECTOR.SUMMARY_LABEL', { name: selectedGroup.name, count: selectedGroup.contacts_count || 0 }) }}
          </span>
          <woot-button variant="link" size="small" type="button" @click.prevent="clearSelectedGroup">
            {{ $t('CAMPAIGN.ADD.CONTACTS.GROUP_SELECTOR.CLEAR') }}
          </woot-button>
        </div>
        <p
          v-if="!contactGroupsLoading && !contactGroups.length"
          class="text-xs text-slate-500 dark:text-slate-400"
        >
          {{ $t('CAMPAIGN.ADD.CONTACTS.GROUP_SELECTOR.EMPTY') }}
        </p>
        <p v-if="isGroupSelectorDisabled" class="text-xs text-amber-600 dark:text-amber-400">
          {{ $t('CAMPAIGN.ADD.CONTACTS.GROUP_SELECTOR.DISABLED_HINT') }}
        </p>
        <p v-else class="text-xs text-slate-500 dark:text-slate-400">
          {{ $t('CAMPAIGN.ADD.CONTACTS.GROUP_SELECTOR.HELP') }}
        </p>
      </div>

      <div v-if="templateRequiresMedia" class="grid gap-3">
        <label class="woot-form-label">
          {{ $t('CAMPAIGN.ADD.TEMPLATE.MEDIA_UPLOAD') }}
        </label>
        <input
          type="file"
          class="woot-input"
          @change="onTemplateMediaChange"
        />
        <div v-if="templateMediaFile" class="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-200">
          <span>{{ templateMediaFile.name }}</span>
          <woot-button variant="link" size="small" type="button" @click.prevent="clearTemplateMedia">
            {{ $t('CAMPAIGN.ADD.TEMPLATE.MEDIA_CLEAR') }}
          </woot-button>
        </div>
        <p class="text-xs text-slate-500 dark:text-slate-400">
          {{ $t('CAMPAIGN.ADD.TEMPLATE.MEDIA_HELP') }}
        </p>
      </div>

      <div class="grid gap-3">
        <label class="woot-form-label">
          {{ $t('CAMPAIGN.ADD.TEMPLATE.SCHEDULING') }}
        </label>
        <div
          class="flex flex-wrap gap-2"
          role="radiogroup"
          :aria-label="$t('CAMPAIGN.ADD.TEMPLATE.SCHEDULING')"
        >
          <label
            class="inline-flex items-center gap-2 cursor-pointer rounded-lg border px-4 py-2.5 text-sm font-medium transition-colors"
            :class="
              sendNow
                ? 'border-n-brand bg-n-brand/10 text-n-brand'
                : 'border-n-weak bg-n-background text-n-slate-12 hover:bg-n-alpha-black1 dark:hover:bg-n-alpha-black2'
            "
          >
            <input
              v-model="sendNow"
              type="radio"
              :value="true"
              class="sr-only"
            />
            <fluent-icon
              icon="send"
              size="18"
              class="shrink-0"
              :class="sendNow ? 'text-n-brand' : 'text-n-slate-11'"
            />
            <span>{{ $t('CAMPAIGN.ADD.TEMPLATE.SEND_NOW') }}</span>
          </label>
          <label
            class="inline-flex items-center gap-2 cursor-pointer rounded-lg border px-4 py-2.5 text-sm font-medium transition-colors"
            :class="
              !sendNow
                ? 'border-n-brand bg-n-brand/10 text-n-brand'
                : 'border-n-weak bg-n-background text-n-slate-12 hover:bg-n-alpha-black1 dark:hover:bg-n-alpha-black2'
            "
          >
            <input
              v-model="sendNow"
              type="radio"
              :value="false"
              class="sr-only"
            />
            <fluent-icon
              icon="calendar-clock"
              size="18"
              class="shrink-0"
              :class="!sendNow ? 'text-n-brand' : 'text-n-slate-11'"
            />
            <span>{{ $t('CAMPAIGN.ADD.TEMPLATE.SCHEDULE') }}</span>
          </label>
        </div>
        <div v-if="!sendNow" class="max-w-sm">
          <WootDateTimePicker
            :value="scheduledAt"
            :confirm-text="$t('CAMPAIGN.ADD.TEMPLATE.DATETIME_CONFIRM')"
            :placeholder="$t('CAMPAIGN.ADD.TEMPLATE.DATETIME_PLACEHOLDER')"
            @change="value => (scheduledAt = value)"
          />
          <span v-if="v$.scheduledAt.$error" class="message">
            {{ $t('CAMPAIGN.ADD.TEMPLATE.SCHEDULE_REQUIRED') }}
          </span>
        </div>
      </div>

      <!-- Auto Resolve Conversation -->
      <div class="grid gap-3">
        <label class="flex items-center gap-2 cursor-pointer">
          <input
            v-model="autoResolveConversation"
            type="checkbox"
            class="woot-checkbox"
          />
          <span class="text-sm text-slate-700 dark:text-slate-200">
            {{ $t('CAMPAIGN.ADD.TEMPLATE.AUTO_RESOLVE_CONVERSATION.LABEL') }}
          </span>
        </label>
        <p class="text-xs text-slate-500 dark:text-slate-400">
          {{ $t('CAMPAIGN.ADD.TEMPLATE.AUTO_RESOLVE_CONVERSATION.HELP') }}
        </p>
      </div>

      <div class="flex items-center gap-2">
        <woot-button
          variant="clear"
          type="button"
          class="text-slate-600"
          @click.prevent="onClose"
        >
          {{ $t('CAMPAIGN.ADD.CANCEL_BUTTON_TEXT') }}
        </woot-button>
        <!-- BOTÃO PRÉ-VISUALIZAR - COMENTADO
        <woot-button
          type="button"
          variant="secondary"
          class="ml-auto"
          :loading="previewLoading || uiFlags.isPreviewing"
          :disabled="!canPreview"
          @click="onPreview"
        >
          {{ $t('CAMPAIGN.ADD.TEMPLATE.PREVIEW_BUTTON') }}
        </woot-button>
        -->
        <woot-button
          type="submit"
          class="ml-auto"
          :is-loading="uiFlags.isCreating"
          :disabled="uiFlags.isCreating || !hasAudience"
        >
          {{ $t('CAMPAIGN.ADD.FORM.SUBMIT') }}
        </woot-button>
      </div>
    </form>

    <div v-if="previewResults.length" class="mt-6">
      <h4 class="font-semibold text-sm text-slate-700 dark:text-slate-200 mb-2">
        {{ $t('CAMPAIGN.ADD.TEMPLATE.PREVIEW_SAMPLE_TITLE') }}
      </h4>
      <div class="overflow-hidden border border-slate-200 dark:border-slate-700 rounded">
        <table class="min-w-full divide-y divide-slate-200 dark:divide-slate-700 text-sm">
          <thead class="bg-slate-50 dark:bg-slate-800/50 text-left">
            <tr>
              <th class="px-4 py-2 text-slate-600 dark:text-slate-300">
                {{ $t('CAMPAIGN.ADD.TEMPLATE.PREVIEW_PHONE') }}
              </th>
              <th class="px-4 py-2 text-slate-600 dark:text-slate-300">
                {{ $t('CAMPAIGN.ADD.TEMPLATE.PREVIEW_SOURCE') }}
              </th>
              <th class="px-4 py-2 text-slate-600 dark:text-slate-300">
                {{ $t('CAMPAIGN.ADD.TEMPLATE.PREVIEW_VARIABLES') }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-200 dark:divide-slate-700 bg-white dark:bg-slate-900">
            <tr v-for="row in previewResults" :key="row.phone">
              <td class="px-4 py-2 text-slate-700 dark:text-slate-200">
                {{ row.phone }}
              </td>
              <td class="px-4 py-2 text-slate-500 capitalize">
                {{ row.source }}
              </td>
              <td class="px-4 py-2">
                <div class="space-y-1 text-xs">
                  <div v-for="(value, key) in row.params" :key="key">
                    <span class="font-medium text-slate-600 dark:text-slate-300">
                      {{ formatPlaceholder(key) }}:
                    </span>
                    <span class="text-slate-600 dark:text-slate-200">
                      {{ value }}
                    </span>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
